import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../config/apiConfig';
import { toast } from 'react-toastify';
import '../styles/VoucherEntryForm.css';
import Swal from 'sweetalert2';
import AirDatePicker from '../components/ui/AirDatePicker';

const VoucherEntryForm = () => {
    const { typeId } = useParams();
    const navigate = useNavigate();
    const [voucherType, setVoucherType] = useState(null);
    const [loading, setLoading] = useState(true);
    const [accounts, setAccounts] = useState([]);
    const [companyBanks, setCompanyBanks] = useState([]); // New state for dynamic banks

    const [entries, setEntries] = useState([
        {
            account: '',
            paymentDate: new Date().toISOString().split('T')[0],
            paymentAmount: '',
            paymentType: 'Credit',
            bank: '',
            checkNumber: '',
            checkDate: '',
            remark: ''
        }
    ]);

    const isBankType = voucherType?.voucherTypeName?.toLowerCase().includes('bank') || 
                       voucherType?.voucherTypeName?.toLowerCase().includes('kasar') ||
                       voucherType?.voucherTypeName?.toLowerCase().includes('kesar');

    const vtName = (voucherType?.voucherTypeName || '').trim().toLowerCase();
    const isSystemReceipt = vtName === 'cash receipt' || vtName === 'bank receipt';
    const isSystemPayment = vtName === 'cash payment' || vtName === 'bank payment';
    const defaultPaymentType = isSystemReceipt ? 'Credit' : (isSystemPayment ? 'Debit' : 'Credit');

    useEffect(() => {
        const fetchData = async () => {
            try {
                const token = sessionStorage.getItem('token');
                const user = JSON.parse(sessionStorage.getItem('user'));
                const headers = { Authorization: `Bearer ${token}` };

                // Parallel fetch for all account types + Banks
                const cid = user.company_id || user.companyId;
                const [typeRes, custRes, sellRes, transRes, bankRes] = await Promise.all([
                    axios.get(`${API_BASE_URL}/voucher-type/${typeId}`, { headers }),
                    axios.get(`${API_BASE_URL}/customer`, { headers, params: { companyId: cid, limit: 1000 } }).catch(() => ({ data: {} })),
                    axios.get(`${API_BASE_URL}/seller`, { headers, params: { companyId: cid, limit: 1000 } }).catch(() => ({ data: {} })),
                    axios.get(`${API_BASE_URL}/transporter`, { headers, params: { companyId: cid, limit: 1000 } }).catch(() => ({ data: {} })),
                    axios.get(`${API_BASE_URL}/bank`, { headers, params: { companyId: cid, limit: 1000 } }).catch(() => ({ data: {} }))
                ]);

                if (typeRes.data) {
                    setVoucherType(typeRes.data);
                    const name = (typeRes.data.voucherTypeName || '').trim().toLowerCase();
                    const sysReceipt = name === 'cash receipt' || name === 'bank receipt';
                    const sysPayment = name === 'cash payment' || name === 'bank payment';
                    const defType = sysReceipt ? 'Credit' : (sysPayment ? 'Debit' : 'Credit');
                    
                    // Reset entries when voucher type changes
                    setEntries([
                        {
                            account: '',
                            paymentDate: new Date().toISOString().split('T')[0],
                            paymentAmount: '',
                            paymentType: defType,
                            bank: '',
                            checkNumber: '',
                            checkDate: '',
                            remark: ''
                        }
                    ]);
                }
                if (bankRes.data?.banks) setCompanyBanks(bankRes.data.banks);

                // Unified Account List
                const allAccounts = [
                    ...(custRes.data.customers || []).map(c => ({ id: c._id, name: `${c.customerName} (Customer)`, group: 'Customer' })),
                    ...(sellRes.data.sellers || []).map(s => ({ id: s._id, name: `${s.sellerName} (Seller)`, group: 'Seller' })),
                    ...(transRes.data.transporters || []).map(t => ({ id: t._id, name: `${t.transporterName} (Transporter)`, group: 'Transporter' }))
                ];
                setAccounts(allAccounts);

            } catch (err) {
                console.error("Error fetching data:", err);
                toast.error("Failed to load required data");
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [typeId]);

    const handleAddMore = () => {
        setEntries([
            ...entries,
            {
                account: '',
                paymentDate: new Date().toISOString().split('T')[0],
                paymentAmount: '',
                paymentType: defaultPaymentType,
                bank: '',
                checkNumber: '',
                checkDate: '',
                remark: ''
            }
        ]);
    };

    const handleRemove = (index) => {
        if (entries.length === 1) return;
        const newEntries = entries.filter((_, i) => i !== index);
        setEntries(newEntries);
    };

    const handleChange = (index, field, value) => {
        const newEntries = [...entries];
        newEntries[index][field] = value;
        setEntries(newEntries);
    };

    const handleSubmit = async (e) => {

    if(e) e.preventDefault();
    const conf = await Swal.fire({
      title: 'Save Data?',
      text: 'Are you sure you want to save this data?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#00334e',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, Save',
      cancelButtonText: 'No, Keep Editing'
    });
    if(!conf.isConfirmed) return;
        e.preventDefault();
        try {
            const token = sessionStorage.getItem('token');
            const user = JSON.parse(sessionStorage.getItem('user'));
            
            const payload = {
                voucherType: typeId,
                entries: entries,
                company: user.company_id || user.companyId
            };

            await axios.post(`${API_BASE_URL}/voucher-entry`, payload, {
                headers: { Authorization: `Bearer ${token}` }
            });

            toast.success("Voucher entries saved successfully!");
            setEntries([{
                account: '',
                paymentDate: new Date().toISOString().split('T')[0],
                paymentAmount: '',
                paymentType: defaultPaymentType,
                bank: '',
                checkNumber: '',
                checkDate: '',
                remark: ''
            }]);
        } catch (err) {
            console.error("Error saving entries:", err);
            toast.error("Failed to save entries");
        }
    };

    if (loading) return <div className="loading">Loading...</div>;

    return (
        <div className="voucher-entry-container">
            <h2 className="voucher-entry-title">Add {voucherType?.voucherTypeName || 'Voucher'} Entry</h2>
            
            <form onSubmit={handleSubmit} className="voucher-entry-form">
                {entries.map((entry, idx) => (
                    <div key={idx} className="voucher-entry-row-card">
                        <div className="voucher-entry-row">
                            <div className="ve-field">
                                <label>Account</label>
                                <div className="ve-select-wrapper">
                                    <select 
                                        value={entry.account}
                                        onChange={(e) => handleChange(idx, 'account', e.target.value)}
                                        required 
                                        className="ve-account-select"
                                    >
                                        <option value="">Select Account</option>
                                        {['Customer', 'Seller', 'Transporter'].map(group => (
                                            <optgroup label={group.toUpperCase()} key={group}>
                                                {accounts.filter(a => a.group === group).map(acc => (
                                                    <option key={acc.id} value={acc.name}>{acc.name}</option>
                                                ))}
                                            </optgroup>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="ve-field">
                                <label>Payment Date</label>
                                <AirDatePicker
                                    value={entry.paymentDate}
                                    onChange={(dateStr) => handleChange(idx, 'paymentDate', dateStr)}
                                    required
                                />
                            </div>
                            <div className="ve-field">
                                <label>Payment Amount</label>
                                <input 
                                    type="number" 
                                    placeholder="Payment Amount"
                                    value={entry.paymentAmount}
                                    onChange={(e) => handleChange(idx, 'paymentAmount', e.target.value)}
                                    required 
                                />
                            </div>
                            <div className="ve-field">
                                <label>Payment Type</label>
                                <select 
                                    value={entry.paymentType}
                                    onChange={(e) => handleChange(idx, 'paymentType', e.target.value)}
                                >
                                    {!isSystemPayment && <option value="Credit">Credit</option>}
                                    {!isSystemReceipt && <option value="Debit">Debit</option>}
                                </select>
                            </div>
                        </div>

                        {/* Condition row for Bank/Check fields */}
                        {isBankType && (
                            <div className="voucher-entry-bank-row">
                                <div className="ve-field">
                                    <label>Bank</label>
                                    <select 
                                        value={entry.bank}
                                        onChange={(e) => handleChange(idx, 'bank', e.target.value)}
                                        required={isBankType}
                                    >
                                        <option value="">Select Bank</option>
                                        {companyBanks.map(bank => (
                                            <option key={bank._id} value={bank.bankName}>{bank.bankName}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="ve-field">
                                    <label>Check Number</label>
                                    <input 
                                        type="text" 
                                        placeholder="Payment Check Number"
                                        value={entry.checkNumber}
                                        onChange={(e) => handleChange(idx, 'checkNumber', e.target.value)}
                                        required={isBankType}
                                    />
                                </div>
                                <div className="ve-field">
                                    <label>Check Date</label>
                                    <AirDatePicker
                                        value={entry.checkDate}
                                        onChange={(dateStr) => handleChange(idx, 'checkDate', dateStr)}
                                        required={isBankType}
                                    />
                                </div>
                                <div className="ve-field">
                                    <label>Remark</label>
                                    <input 
                                        type="text" 
                                        placeholder="Enter Remark"
                                        value={entry.remark}
                                        onChange={(e) => handleChange(idx, 'remark', e.target.value)}
                                    />
                                </div>
                            </div>
                        )}

                        {!isBankType && (
                            <div className="voucher-entry-remark-row">
                                <div className="ve-field ve-field--full">
                                    <label>Remark</label>
                                    <input 
                                        type="text" 
                                        placeholder="Enter Remark"
                                        value={entry.remark}
                                        onChange={(e) => handleChange(idx, 'remark', e.target.value)}
                                    />
                                </div>
                                {idx > 0 && (
                                    <button 
                                        type="button" 
                                        className="ve-remove-btn" 
                                        onClick={() => handleRemove(idx)}
                                    >
                                        Remove
                                    </button>
                                )}
                            </div>
                        )}

                        {isBankType && idx > 0 && (
                            <div className="ve-remove-wrapper">
                                <button 
                                    type="button" 
                                    className="ve-remove-btn" 
                                    onClick={() => handleRemove(idx)}
                                >
                                    Remove
                                </button>
                            </div>
                        )}
                    </div>
                ))}

                <div className="voucher-entry-actions">
                    <button type="button" className="ve-add-more-btn" onClick={handleAddMore}>
                        ADD MORE
                    </button>
                    <button type="submit" className="ve-submit-btn">
                        Submit
                    </button>
                </div>
            </form>
        </div>
    );
};

export default VoucherEntryForm;
